import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  getMediaJobs,
  genUpload,
  genTextToImage,
  genImageToImage,
  genTextToVideo,
  genImageToVideo,
  genTalking,
  getMinimaxVoices,
  generateVoice,
  type MemberMediaJob,
  type MediaJobType,
  type DbMinimaxVoice,
} from '@/api/astraea';
import { spendCredits, spendCreditsUnits, getCreditCosts } from '@/api/auth';
import { DEFAULT_PORTRAIT_ASPECT, shouldForcePersonFraming, shouldPreserveIdentity, shouldUseSceneComposite } from '@/lib/imagePrompts';
import MinimaxVoiceSelect from '@/components/MinimaxVoiceSelect';
import { useAskConfirm, usePaidGenerationGuard } from '@/hooks/usePaidGenerationGuard';
import { PAID_GEN_START_CONFIRM } from '@/lib/paidGeneration';

const TABS: { key: MediaJobType; label: string; icon: string; action: string; kind: 'image' | 'video'; needImage: boolean; optionalImage?: boolean; needVoice?: boolean }[] = [
  { key: 'text_to_image', label: '文生图', icon: 'ri-image-add-line', action: 'gen_t2i', kind: 'image', needImage: false },
  { key: 'image_to_image', label: '图生图', icon: 'ri-image-edit-line', action: 'gen_i2i', kind: 'image', needImage: false, optionalImage: true },
  { key: 'text_to_video', label: '文生影片', icon: 'ri-film-line', action: 'gen_t2v', kind: 'video', needImage: false },
  { key: 'image_to_video', label: '图生影片', icon: 'ri-clapperboard-line', action: 'gen_i2v', kind: 'video', needImage: true },
  { key: 'talking_avatar', label: '图生口播视频', icon: 'ri-user-voice-line', action: 'gen_talking', kind: 'video', needImage: true, needVoice: true },
];

const ASPECTS = ['1:1', '3:4', '4:3', '9:16', '16:9'];

const JOB_LABEL: Record<MediaJobType, string> = {
  text_to_image: '文生图',
  image_to_image: '图生图',
  text_to_video: '文生影片',
  image_to_video: '图生影片',
  talking_avatar: '图生口播视频',
  voice_generation: '配音',
};

async function downloadFromUrl(filename: string, url: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(u);
  } catch {
    window.open(url, '_blank');
  }
}

function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function StudioPage() {
  const { askConfirm, confirmDialog } = useAskConfirm();
  const [tab, setTab] = useState<MediaJobType>('text_to_image');
  const [prompt, setPrompt] = useState('');
  const [aspect, setAspect] = useState(DEFAULT_PORTRAIT_ASPECT);
  const [inputUrl, setInputUrl] = useState<string | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'info' | 'error' | 'ok'; text: string } | null>(null);
  const [jobs, setJobs] = useState<MemberMediaJob[]>([]);
  const [costs, setCosts] = useState<Record<string, number>>({});
  const [nowMs, setNowMs] = useState(Date.now());
  const [genStartMs, setGenStartMs] = useState(0);
  const [voices, setVoices] = useState<DbMinimaxVoice[]>([]);
  const [voiceId, setVoiceId] = useState('');
  const [speed, setSpeed] = useState(1);
  const [pitch, setPitch] = useState(0);
  // 图生口播时长 = 讲稿念完的时间(中文口播约每秒 4 字)。不强制设定秒数,只即时估算给用户参考。
  const talkEstSec = Math.max(1, Math.round(prompt.trim().length / 4));
  const [trialUrl, setTrialUrl] = useState<string | null>(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [paidSessionActive, setPaidSessionActive] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const cfg = TABS.find((t) => t.key === tab)!;

  const load = useCallback(async () => {
    try {
      const j = await getMediaJobs(60);
      setJobs(j);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getMinimaxVoices().then((vs) => { setVoices(vs); setVoiceId((p) => p || (vs[0]?.voice_id ?? '')); }).catch(() => {});
    getCreditCosts().then((cs) => {
      const m: Record<string, number> = {};
      for (const c of cs) m[c.action] = c.cost;
      setCosts(m);
    }).catch(() => {});
  }, []);

  const hasProcessing = jobs.some((j) => j.display_status === 'processing' || j.display_status === 'pending');

  usePaidGenerationGuard({
    isGenerating: busy || hasProcessing || paidSessionActive,
    label: JOB_LABEL[tab],
    askConfirm,
  });

  // 有影片任务仍在生成中 → 自动轮询直到完成
  useEffect(() => {
    if (!hasProcessing) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [hasProcessing, load]);

  // 进度计时:生成中(同步图片)或有影片仍在处理时,每秒更新一次「已等候秒数」
  useEffect(() => {
    if (!busy && !hasProcessing) return;
    const t = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [busy, hasProcessing]);

  const formElapsed = busy && genStartMs ? Math.max(0, Math.floor((nowMs - genStartMs) / 1000)) : 0;

  const switchTab = (k: MediaJobType) => {
    setTab(k);
    setNote(null);
    setInputUrl(null);
    const t = TABS.find((x) => x.key === k);
    if (t?.kind === 'image') setAspect(DEFAULT_PORTRAIT_ASPECT);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadBusy(true);
    setNote(null);
    try {
      const dataUrl = await fileToDataURL(file);
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const r = await genUpload(dataUrl, ext);
      if (r.ok && r.url) { setInputUrl(r.url); }
      else setNote({ tone: 'error', text: r.message || '图片上传失败,请重试' });
    } catch (err) {
      setNote({ tone: 'error', text: err instanceof Error ? err.message : '图片上传失败' });
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // 免费试听(不扣点、不绑定):用选的声音+语速语调念一小段试听
  const handleTrial = async () => {
    if (!voiceId) { setNote({ tone: 'error', text: '请先选一个声音' }); return; }
    setTrialBusy(true); setTrialUrl(null);
    try {
      const sample = prompt.trim().slice(0, 60) || '你好,这是这个声音的试听,可以调语速和语调。';
      const r = await generateVoice(null, null, voiceId, sample, speed, pitch);
      if (r.ok && r.url) setTrialUrl(r.url);
      else setNote({ tone: 'error', text: r.message || '试听失败,请再试一次' });
    } catch (e) {
      setNote({ tone: 'error', text: e instanceof Error ? e.message : '试听失败' });
    } finally {
      setTrialBusy(false);
    }
  };

  const handleGenerate = async () => {
    if (cfg.kind === 'image' || cfg.key === 'text_to_video' || cfg.key === 'talking_avatar') {
      if (!prompt.trim()) { setNote({ tone: 'error', text: cfg.key === 'talking_avatar' ? '请先输入要讲的脚本' : '请先输入描述' }); return; }
    }
    if (cfg.needImage && !inputUrl) { setNote({ tone: 'error', text: cfg.key === 'talking_avatar' ? '请先上传一张清楚正面的人脸图' : '请先上传一张图片' }); return; }
    if (cfg.needVoice && !voiceId) { setNote({ tone: 'error', text: '请先选一个声音' }); return; }

    // 图生口播按时长计费(讲稿越长越贵);其余动作固定单价
    const isTalk = cfg.key === 'talking_avatar';
    const base = costs[cfg.action] ?? (cfg.kind === 'video' ? 30 : 8);
    const units = isTalk ? Math.max(1, Math.ceil(prompt.trim().length / 120)) : 1;
    const cost = base * units;
    const unitNote = units > 1 ? `\n(约 ${units * 30} 秒以内,按时长计:${units} 段 × ${base} 点)` : '';
    if (!(await askConfirm('确认生成', PAID_GEN_START_CONFIRM(cfg.label, cost, unitNote), { confirmLabel: '确定生成', cancelLabel: '取消' }))) return;

    setNote({ tone: 'info', text: '正在送出请求…' });
    try { if (isTalk) await spendCreditsUnits(cfg.action, units); else await spendCredits(cfg.action); } catch (e) { setNote({ tone: 'error', text: e instanceof Error ? e.message : '点数不足' }); return; }

    setPaidSessionActive(true);

    setGenStartMs(Date.now());
    setNowMs(Date.now());
    setBusy(true);
    try {
      if (cfg.key === 'text_to_image') {
        const r = await genTextToImage(prompt.trim(), aspect);
        if (r.ok && r.url) setNote({ tone: 'ok', text: '生成完成!已加入下方结果。' });
        else setNote({ tone: 'error', text: r.message || '生成失败' });
      } else if (cfg.key === 'image_to_image') {
        const r = await genImageToImage(prompt.trim(), aspect, inputUrl);
        if (r.ok && r.url) {
          const lockHint = inputUrl && shouldPreserveIdentity(prompt.trim(), true) ? '（已保持同一张脸与同一身形）' : inputUrl && shouldUseSceneComposite(prompt.trim(), true) ? '（随机新亚洲人置入背景场景）' : '';
          setNote({ tone: 'ok', text: `生成完成!已加入下方结果。${lockHint}` });
        } else setNote({ tone: 'error', text: r.message || '生成失败' });
      } else if (cfg.key === 'text_to_video') {
        const r = await genTextToVideo(prompt.trim());
        if (r.ok && r.started) setNote({ tone: 'ok', text: '影片已开始生成(约 2-5 分钟),可离开此页,完成后会自动出现在下方结果。' });
        else setNote({ tone: 'error', text: r.message || '提交失败' });
      } else if (cfg.key === 'talking_avatar') {
        const r = await genTalking(inputUrl as string, prompt.trim(), voiceId, speed, pitch);
        if (r.ok && r.started) setNote({ tone: 'ok', text: '口播影片已开始生成(建脸+对口型,约 3-6 分钟),可离开此页,完成后会自动出现在下方结果。' });
        else setNote({ tone: 'error', text: r.message || '提交失败' });
      } else {
        const r = await genImageToVideo(prompt.trim() || '画面自然流畅、光线柔和', inputUrl as string);
        if (r.ok && r.started) setNote({ tone: 'ok', text: '影片已开始生成(约 2-5 分钟),可离开此页,完成后会自动出现在下方结果。' });
        else setNote({ tone: 'error', text: r.message || '提交失败' });
      }
      await load();
    } catch (e) {
      setNote({ tone: 'error', text: e instanceof Error ? e.message : '生成失败' });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!paidSessionActive) return;
    if (!busy && !hasProcessing) setPaidSessionActive(false);
  }, [busy, hasProcessing, paidSessionActive]);

  const baseCost = costs[cfg.action] ?? (cfg.kind === 'video' ? 30 : 8);
  const costUnits = cfg.key === 'talking_avatar' ? Math.max(1, Math.ceil(prompt.trim().length / 120)) : 1;
  const cost = baseCost * costUnits;
  const i2iLockFace = cfg.key === 'image_to_image' && !!inputUrl && shouldPreserveIdentity(prompt.trim(), true);
  const i2iScene = cfg.key === 'image_to_image' && !!inputUrl && shouldUseSceneComposite(prompt.trim(), true);
  const isExplicitPersonPrompt = shouldForcePersonFraming(prompt.trim());

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <div className="flex items-center gap-2 text-xs text-secondary-400 mb-2">
        <Link to="/board" className="hover:text-foreground-600">工作台</Link>
        <i className="ri-arrow-right-s-line"></i><span>生成中心</span>
      </div>
      <h1 className="text-2xl font-bold text-foreground-950 mb-1">生成中心</h1>
      <p className="text-sm text-foreground-600 mb-1">不绑角色的通用生成工具:文字或图片,一键生成图片或影片。</p>
      <p className="text-xs text-amber-600 mb-6"><i className="ri-time-line"></i> 生成的素材只保留 30 天,逾期会自动删除,请及时下载保存。</p>

      {/* 分页 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => switchTab(t.key)}
            className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium border transition-colors cursor-pointer ${tab === t.key ? 'bg-primary-500 text-background-50 border-primary-500' : 'bg-background-50 text-foreground-600 border-secondary-200 hover:border-secondary-300'}`}
          >
            <i className={t.icon}></i>{t.label}
          </button>
        ))}
      </div>

      {/* 生成表单 */}
      <div className="rounded-2xl border border-secondary-200 bg-background-50 p-5 mb-8">
        {(cfg.needImage || cfg.optionalImage) && (
          <div className="mb-4">
            <div className="text-xs font-medium text-foreground-700 mb-1.5">参考图片{cfg.kind === 'video' ? '(首帧)' : cfg.optionalImage ? '(可选)' : ''}</div>
            {inputUrl ? (
              <div className="flex items-center gap-3">
                <img src={inputUrl} alt="input" className="w-20 h-20 rounded-lg object-cover border border-secondary-200" />
                <button onClick={() => setInputUrl(null)} className="text-xs text-secondary-500 hover:text-red-500 cursor-pointer"><i className="ri-delete-bin-line"></i> 移除</button>
              </div>
            ) : (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploadBusy}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-secondary-300 text-sm text-foreground-600 hover:border-primary-400 cursor-pointer"
              >
                <i className="ri-upload-2-line"></i>{uploadBusy ? '上传中…' : '上传图片'}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
            {cfg.key === 'image_to_image' && (
              <p className="mt-1.5 text-[11px] text-secondary-500 leading-relaxed">
                {inputUrl
                  ? (i2iLockFace
                    ? <>你写了「这个男生 / 同一人」等 → <b>锁脸锁身形</b>，脸与体型须与参考图一致，不会忽胖忽瘦。</>
                    : i2iScene
                      ? <>风景/背景参考 → 随机生成<b>新的亚洲人</b>置入场景，不锁脸、不重复同一人。</>
                      : <>有参考图但未要求同一人 → 不锁脸;按你的文字描述处理参考图。</>)
                  : <>未上传参考图 → 按文字描述生成;只有明确写人物、人像、男生、女生、模特等请求时才加人像约束。</>}
              </p>
            )}
          </div>
        )}

        <div className="text-xs font-medium text-foreground-700 mb-1.5">
          {cfg.key === 'talking_avatar' ? '要讲的脚本(这张脸会照这段话对口型讲出来)' : cfg.key === 'image_to_video' ? '动态描述(可留空,系统会用预设让画面自然动起来)' : '描述你想要的内容'}
        </div>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          placeholder={cfg.kind === 'video' ? '例:一只橘色小猫在阳光下慢慢眨眼,轻轻摆动尾巴,温馨写实' : cfg.key === 'image_to_image' ? '例:数据中心环境 / 或：一位男生,三分之四侧面半身照(需上传人物照才锁脸)' : '例:数据中心环境 / 或：一位 28 岁中国男生在咖啡馆窗边,半身照'}
          className="w-full px-3 py-2.5 rounded-lg border border-secondary-200 bg-background-100 text-sm focus:outline-none focus:border-primary-400 resize-none"
        />
        {cfg.kind === 'image' && cfg.key !== 'image_to_image' && (
          <p className="mt-1.5 text-[11px] text-secondary-500 leading-relaxed">文生图按描述生成;只有明确写人物、人像、男生、女生、模特等请求时,才加入人像构图约束。</p>
        )}
        {cfg.key === 'image_to_image' && (
          <p className="mt-1.5 text-[11px] text-secondary-500 leading-relaxed">
            {i2iLockFace
              ? <>本次<b>锁脸+锁身形</b>：同一张脸、同一身材比例，只换角度/造型。比例 {aspect}。</>
              : i2iScene
                ? <>本次<b>场景置入</b>：随机新亚洲人放入背景，不锁脸。比例 {aspect}。</>
                : <>默认<b>不锁脸</b>。{inputUrl ? '参考图不绑定脸型。' : '按文字描述生成。'}比例 {aspect}。</>}
          </p>
        )}

        {cfg.key === 'talking_avatar' && prompt.trim().length > 0 && (
          <div className="mt-1.5 text-[11px] text-secondary-400">
            讲稿 {prompt.trim().length} 字,预计口播时长 ≈ <span className="font-medium text-foreground-600">{talkEstSec} 秒</span>,约扣 <span className="font-medium text-foreground-600">{cost} 点</span>(按时长计费:越长越贵;角色会把整段念完,不必特别设定秒数)
            {talkEstSec > 120 ? <span className="text-amber-600">　·　较长的影片生成更久、成本更高</span> : null}
          </div>
        )}

        {cfg.kind === 'image' && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground-700">比例</span>
            {((cfg.key === 'text_to_image' || cfg.key === 'image_to_image') && isExplicitPersonPrompt && !i2iLockFace && !i2iScene) ? (
              <span className="px-2.5 py-1 rounded-md text-xs border bg-primary-50 text-primary-700 border-primary-300">3:4（半身远景）</span>
            ) : (
              ASPECTS.map((a) => (
                <button
                  key={a}
                  onClick={() => setAspect(a)}
                  className={`px-2.5 py-1 rounded-md text-xs border cursor-pointer ${aspect === a ? 'bg-primary-50 text-primary-700 border-primary-300' : 'bg-background-50 text-secondary-500 border-secondary-200'}`}
                >{a}</button>
              ))
            )}
          </div>
        )}

        {cfg.needVoice && (
          <div className="mt-3 space-y-2.5 rounded-lg border border-secondary-200 bg-background-50 p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-foreground-700">声音</span>
              <MinimaxVoiceSelect
                voices={voices}
                value={voiceId}
                onChange={(id) => { setVoiceId(id); setTrialUrl(null); }}
                showTags
                className="text-sm border border-secondary-200 rounded-lg px-2.5 py-1.5 bg-background-100 max-w-[240px]"
                placeholder="请选择声音"
              />
              <button onClick={handleTrial} disabled={trialBusy} className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium cursor-pointer border ${trialBusy ? 'border-secondary-200 text-secondary-400 cursor-wait' : 'border-primary-300 text-primary-700 hover:bg-primary-50'}`}>
                <i className="ri-play-circle-line"></i>{trialBusy ? '生成试听中…' : '试听'}
              </button>
            </div>
            <div className="flex items-center gap-3 flex-wrap text-[11px] text-secondary-500">
              <label className="flex items-center gap-1.5">语速 <input type="range" min="0.5" max="2" step="0.1" value={speed} onChange={(e) => { setSpeed(parseFloat(e.target.value)); setTrialUrl(null); }} className="w-24" /> <span className="font-mono w-7">{speed.toFixed(1)}x</span></label>
              <label className="flex items-center gap-1.5">语调 <input type="range" min="-12" max="12" step="1" value={pitch} onChange={(e) => { setPitch(parseInt(e.target.value, 10)); setTrialUrl(null); }} className="w-24" /> <span className="font-mono w-7">{pitch}</span></label>
            </div>
            {trialUrl && <audio src={trialUrl} controls autoPlay className="h-8 w-full" />}
            <p className="text-[10px] text-secondary-400">可随时试听、调语速语调;这是单次口播,不会绑定到任何角色。</p>
          </div>
        )}

        {note && (
          <div className={`mt-3 text-xs rounded-lg px-3 py-2 ${note.tone === 'error' ? 'bg-red-50 text-red-600' : note.tone === 'ok' ? 'bg-green-50 text-green-700' : 'bg-primary-50 text-primary-700'}`}>
            {note.text}
          </div>
        )}

        <button
          onClick={handleGenerate}
          disabled={busy || uploadBusy}
          className={`mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-background-50 transition-colors ${busy ? 'bg-primary-400 cursor-wait' : 'bg-primary-500 hover:bg-primary-600 cursor-pointer'}`}
        >
          {busy ? <div className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div> : <i className={cfg.icon}></i>}
          {busy ? '生成中…' : `开始生成 · ${cost} 点`}
        </button>

        {busy && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-[11px] text-primary-600 mb-1">
              <span>{cfg.kind === 'video' ? '正在送出影片请求…' : '正在生成图片…'}</span>
              <span className="font-mono">已等候 {formElapsed}s</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-primary-100 overflow-hidden">
              <div className="h-full bg-primary-400 rounded-full animate-pulse" style={{ width: `${Math.min(95, 12 + formElapsed * 3.5)}%` }}></div>
            </div>
            <p className="mt-1.5 text-[11px] text-secondary-400">图片约 20-30 秒。请稍候,完成后会出现在下方结果。</p>
          </div>
        )}
        {cfg.kind === 'video' && !busy && <p className="mt-2 text-[11px] text-secondary-400">影片为非同步生成,送出后可离开此页,完成后会自动显示在下方。</p>}
      </div>

      {/* 我的生成结果 */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-foreground-950">我的生成结果</h2>
        <button onClick={load} className="text-xs text-secondary-500 hover:text-foreground-700 cursor-pointer"><i className="ri-refresh-line"></i> 重新整理</button>
      </div>
      {jobs.length === 0 ? (
        <div className="py-12 text-center text-sm text-secondary-400">还没有生成结果,上面选一个工具开始吧。</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {jobs.map((j) => {
            const url = j.output_url;
            const isVideo = j.display_type === '文生影片' || j.display_type === '图生影片' || j.display_type === '图生口播视频';
            const done = j.display_status === 'completed';
            const failed = j.display_status === 'failed' || j.display_status === 'timeout';
            const inProgress = j.display_status === 'pending' || j.display_status === 'processing';
            return (
              <div key={j.job_row_id} className="rounded-xl border border-secondary-200 bg-background-50 overflow-hidden">
                <div className="aspect-square bg-secondary-100 flex items-center justify-center">
                  {done && url ? (
                    isVideo
                      ? <video src={url} controls className="w-full h-full object-contain bg-black" />
                      : <img src={url} alt={j.display_type} className="w-full h-full object-cover" />
                  ) : failed ? (
                    <div className="text-xs text-red-500 text-center px-2">
                      <i className="ri-error-warning-line block text-lg mb-1"></i>
                      {j.failed_public_reason || '生成失败'}
                    </div>
                  ) : inProgress ? (() => {
                    const sec = Math.max(0, Math.floor((nowMs - new Date(j.created_at).getTime()) / 1000));
                    const mm = String(Math.floor(sec / 60)).padStart(1, '0');
                    const ss = String(sec % 60).padStart(2, '0');
                    return (
                      <div className="w-full px-3 text-center text-xs text-secondary-500">
                        <div className="w-5 h-5 mx-auto mb-1.5 border-2 border-secondary-300 border-t-primary-500 rounded-full animate-spin"></div>
                        <div>{j.status_label} <span className="font-mono text-primary-600">{mm}:{ss}</span></div>
                        {j.estimated_wait_hint && <div className="mt-1 text-[10px] text-secondary-400">{j.estimated_wait_hint}</div>}
                        <div className="mt-2 h-1.5 w-full rounded-full bg-primary-100 overflow-hidden">
                          <div className="h-full bg-primary-400 rounded-full animate-pulse" style={{ width: `${Math.min(95, 8 + sec * 0.5)}%` }}></div>
                        </div>
                        {j.leave_hint && <div className="mt-1.5 text-[10px] text-secondary-400">{j.leave_hint}</div>}
                      </div>
                    );
                  })() : null}
                </div>
                <div className="p-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium text-foreground-700">{j.display_type}</span>
                    {done && url && (
                      <button
                        onClick={() => { downloadFromUrl(`${j.display_type}_${j.job_row_id}.${isVideo ? 'mp4' : 'png'}`, url); setNote({ tone: 'ok', text: '已开始下载。作品保留 30 天,请及时保存!' }); }}
                        className="text-[11px] text-primary-600 hover:text-primary-700 cursor-pointer"
                      ><i className="ri-download-line"></i> 下载</button>
                    )}
                  </div>
                  {j.user_input_preview && (
                    <p className="mt-1 text-[10px] text-secondary-400 line-clamp-2">{j.user_input_preview}</p>
                  )}
                  {done && (() => {
                    const ageMs = Math.max(0, Date.now() - new Date(j.created_at).getTime());
                    const daysLeft = Math.max(0, 30 - Math.floor(ageMs / 86400000));
                    return (
                      <p className={`mt-1 text-[10px] ${daysLeft <= 3 ? 'text-red-500' : 'text-secondary-400'}`}>
                        <i className="ri-time-line"></i> {daysLeft > 0 ? `保留期剩 ${daysLeft} 天` : '已逾保留期'}
                      </p>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {confirmDialog}
    </div>
  );
}
